import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { BRAND } from "./data";
import { Logo } from "./Logo";
import { useTx, txtStyle } from "./siteText";

const LINKS = [
  { label: "Story", href: "#story", type: "anchor" },
  { label: "Menu", href: "/menu", type: "route" },
  { label: "Bar Menu", href: "/drinks", type: "route" },
  { label: "Gallery", href: "#gallery", type: "anchor" },
  { label: "Celebrate", href: "#celebrate", type: "anchor" },
  { label: "Visit", href: "#visit", type: "anchor" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();
  const tx = useTx();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handle = (link) => {
    setOpen(false);
    if (link.type === "route") { navigate(link.href); return; }
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: link.href } });
    else if (lenis) lenis.scrollTo(link.href, { offset: -96, duration: 1.4 });
    else document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-50 bg-cantina-navy transition-shadow duration-500 ${
        scrolled ? "shadow-[0_8px_30px_rgba(7,24,71,0.45)]" : ""
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-[100px] flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="nav-logo" className="relative flex items-center group">
          <span className="absolute -inset-x-5 -inset-y-2 rounded-full bg-cantina-cream/[0.06] blur-xl transition-opacity duration-500 group-hover:bg-cantina-cream/[0.1]" aria-hidden="true" />
          <Logo className="relative h-16 md:h-20 w-auto drop-shadow-[0_2px_14px_rgba(255,255,255,0.28)]" />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => handle(l)}
              className="nav-link font-sans text-sm font-medium tracking-wide text-cantina-cream/80 hover:text-cantina-cream transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href={BRAND.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary bg-cantina-marigold text-cantina-navy font-sans tracking-wide px-6 py-3 rounded-full"
            style={{ boxShadow: "0 10px 24px rgba(233,196,106,0.28)", ...txtStyle(tx("nav.orderLabel")) }}
            data-testid="nav-order-button"
          >
            {tx("nav.orderLabel").text}
          </a>
        </div>

        <button className="md:hidden text-cantina-cream" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-cantina-navydeep border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => handle(l)} className="text-left font-display text-3xl text-cantina-cream" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
                  {l.label}
                </button>
              ))}
              <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary bg-cantina-marigold text-cantina-navy text-center tracking-wide px-6 py-4 rounded-full mt-2" style={txtStyle(tx("nav.orderLabel"))} data-testid="nav-mobile-order">
                {tx("nav.orderLabel").text}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
